// Runtime wiring for the client diagnostics beacon (specs/observability).
// Captures uncaught errors / unhandled rejections / console.error, keeps a
// breadcrumb trail, and flushes batches to Convex `telemetry:report` keyed by
// a short on-screen code. Invariant: telemetry never breaks the app — every
// path here swallows its own failures, and nothing captured while we are
// reporting is re-captured (loop guard).
import { writable, type Writable } from 'svelte/store';
import { getConvex } from './convexClient';
import {
  FLUSH_DEBOUNCE_MS,
  buildReportEvents,
  canFlush,
  describeThrown,
  generateCode,
  pushEvent,
  type TelemetryEvent
} from './telemetryCore';

/** Set once an uncaught error is captured; the AppShell banner subscribes. */
export const telemetryAlert: Writable<{ code: string } | null> = writable(null);

const state = {
  installed: false,
  code: generateCode(),
  crumbs: [] as TelemetryEvent[],
  captured: [] as TelemetryEvent[],
  flushCount: 0,
  flushTimer: null as ReturnType<typeof setTimeout> | null,
  reporting: false
};

export function getDiagnosticsCode(): string {
  return state.code;
}

/** App-level breadcrumb ("route: vidamor", "vidamor standing: canBuy"). */
export function crumb(message: string): void {
  try {
    pushEvent(state.crumbs, { kind: 'crumb', message, at: Date.now() });
  } catch {
    /* never break the app */
  }
}

function capture(kind: 'error' | 'rejection', value: unknown): void {
  if (state.reporting) return;
  try {
    const described = describeThrown(value);
    pushEvent(state.captured, { kind, ...described, at: Date.now() });
    telemetryAlert.set({ code: state.code });
    scheduleFlush();
  } catch {
    /* never break the app */
  }
}

function scheduleFlush(): void {
  if (state.flushTimer || !canFlush(state.flushCount)) return;
  state.flushTimer = setTimeout(() => {
    state.flushTimer = null;
    void flush();
  }, FLUSH_DEBOUNCE_MS);
}

async function flush(): Promise<void> {
  if (!canFlush(state.flushCount) || state.captured.length === 0) return;
  state.flushCount += 1;
  const events = buildReportEvents(state.crumbs, state.captured);
  state.captured = [];
  state.reporting = true;
  try {
    // String function name via `as any` — the established convexApi.ts idiom.
    const convex = getConvex() as any;
    await convex.mutation('telemetry:report', {
      code: state.code,
      url: window.location.href,
      userAgent: navigator.userAgent,
      events
    });
  } catch {
    /* the beacon is best-effort; a failed flush must stay silent */
  } finally {
    state.reporting = false;
  }
}

/** Install global handlers. Idempotent; safe to call before app mount. */
export function initTelemetry(): void {
  if (state.installed || typeof window === 'undefined') return;
  state.installed = true;

  window.addEventListener('error', event => {
    capture('error', event.error ?? event.message);
  });
  window.addEventListener('unhandledrejection', event => {
    capture('rejection', event.reason);
  });

  // console.error becomes a crumb (context for the next batch), never an
  // alert of its own — pages log recoverable failures liberally.
  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    originalConsoleError(...args);
    if (state.reporting) return;
    try {
      const message = args.map(arg => describeThrown(arg).message).join(' ');
      pushEvent(state.crumbs, { kind: 'console', message, at: Date.now() });
    } catch {
      /* never break console.error */
    }
  };
}

/** Capture an app-level fatal (e.g. mount failure) and flush immediately. */
export function reportFatal(value: unknown): string {
  capture('error', value);
  if (state.flushTimer) {
    clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }
  void flush();
  return state.code;
}
